import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert, Send, Check, RefreshCw, Copy } from 'lucide-react';
import { BotDisabledError, TelegramUnavailableError } from '@/lib/api';
import { BotDisabledScreen } from '@/components/BotDisabledScreen';
import { TelegramUnavailableScreen } from '@/components/TelegramUnavailableScreen';
import { autoLog, reportError } from '@/lib/error-report';
import i18n from '@/i18n';

interface Props {
  children: ReactNode;
}

type Phase = 'idle' | 'sending' | 'sent' | 'failed';

interface State {
  error: Error | null;
  phase: Phase;
  code: string;
  description: string;
}

// Класс-компонент — hooks недоступны, поэтому зовём `i18n.t` напрямую. Инициализация
// словаря идёт на импорте `./i18n` в main.tsx, до первого рендера: если бы она упала,
// не смонтировался бы и сам ErrorBoundary, так что тут словарь всегда готов.
// reportCode показываем ПОСЛЕ отправки — тогда он гарантированно есть в БД (юзер
// диктует его разработчику). Тихий авто-лог краша летит сразу.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, phase: 'idle', code: '', description: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    // Тихий авто-лог — даже если юзер просто перезагрузит, краш до нас дойдёт.
    void autoLog(error, { severity: 'crash', componentStack: info.componentStack ?? undefined }).catch(
      () => {},
    );
  }

  private send = async () => {
    const err = this.state.error;
    if (!err) return;
    this.setState({ phase: 'sending' });
    const res = await reportError(err, {
      kind: 'user_report',
      severity: 'crash',
      description: this.state.description.trim() || undefined,
      componentStack: undefined,
    });
    this.setState({ phase: res.ok ? 'sent' : 'failed', code: res.reportCode });
  };

  private copyCode = () => {
    navigator.clipboard?.writeText(this.state.code).catch(() => {});
  };

  render() {
    if (!this.state.error) return this.props.children;
    const err = this.state.error;
    // Telegram не отдал данные для входа — не наша поломка, отдельный экран.
    if (err instanceof TelegramUnavailableError) {
      return <TelegramUnavailableScreen reason={err.reason} />;
    }
    // Block B.9: специальный экран для 401/403.
    if (err instanceof BotDisabledError) {
      return <BotDisabledScreen />;
    }

    const { phase, code, description } = this.state;
    const done = phase === 'sent' || phase === 'failed';

    return (
      <div className="min-h-screen bg-background text-foreground p-6 flex flex-col items-center justify-center gap-5 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(240,101,106,0.14)' }}
        >
          <TriangleAlert size={30} color="var(--color-destructive)" />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-bold">{i18n.t('Crash title')}</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {err.message || i18n.t('Unknown error')}
          </p>
        </div>

        {done ? (
          <div className="w-full max-w-sm space-y-4">
            <p className="text-sm text-muted-foreground">
              {phase === 'sent'
                ? i18n.t('Thanks, developers will look into it')
                : i18n.t('Could not send, save the code')}
            </p>
            <div className="rounded-xl bg-white/6 border border-white/10 px-4 py-3 text-left">
              <div className="text-[11px] uppercase text-white/40 mb-1">{i18n.t('Report code')}</div>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-lg font-bold tracking-wider select-all">{code}</span>
                <button
                  type="button"
                  onClick={this.copyCode}
                  className="flex items-center gap-1 text-xs text-accent active:scale-95 transition-transform"
                >
                  <Copy size={14} /> {i18n.t('Copy')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-sm space-y-3">
            <textarea
              value={description}
              onChange={(e) => this.setState({ description: e.target.value })}
              placeholder={i18n.t('Describe what happened (optional)')}
              rows={3}
              maxLength={2000}
              className="w-full rounded-xl bg-white/6 border border-white/10 px-4 py-3 text-sm text-foreground placeholder:text-white/35 resize-none focus:outline-none focus:border-white/25"
            />
            <button
              type="button"
              onClick={this.send}
              disabled={phase === 'sending'}
              className="w-full calm-cta py-3 rounded-xl text-sm font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send size={16} />
              {phase === 'sending' ? i18n.t('Sending…') : i18n.t('Send report to developers')}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="flex items-center justify-center gap-2 text-sm font-semibold text-white/60 active:text-white transition-colors"
        >
          <RefreshCw size={15} /> {i18n.t('Reload page')}
        </button>
      </div>
    );
  }
}
